import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Dialog, { DialogFooter, DialogHeader } from '../../../../bootstrap/Dialog.jsx';
import TextInput from '../../../../common/TextInput.jsx';
import FormulaEditor from '../../../../common/FormulaEditor/FormulaEditor.jsx';
import { selectWidgetEffectiveCols } from '../../../redux/datasourceSlice.js';
import { compileFormula } from '../../../../../helpers/chart_formula/index.js';
import { useFormulaFieldSettings } from './useFormulaFieldSettings.js';
import styles from './FormulaField.module.scss';

/**
 * Create or edit a widget formula field.
 * Calls `onSaved(field)` after saving, so the caller can e.g. select the new field as yKey.
 */
function FormulaFieldDialog({ widgetId, field = null, onSaved, closeDialog }) {
  const cols = useSelector(selectWidgetEffectiveCols(widgetId));
  const { addFormulaField, updateFormulaField } = useFormulaFieldSettings(widgetId);
  const [name, setName] = useState(field?.name ?? '');
  const [formula, setFormula] = useState(field?.formula ?? '');

  // Formula fields can only reference datasource columns, not other formula fields
  const variables = useMemo(
    () => cols.map(c => ({ name: c.name, accessor: c.accessor, dataType: c.data_type })),
    [cols]
  );

  const { error, references } = compileFormula(formula);
  const unknownColumns = useMemo(() => {
    const known = new Set(cols.map(c => c.accessor));
    return references.filter(r => !known.has(r));
  }, [references, cols]);

  const canSave = name.trim() !== '' && !error;

  const handleSave = () => {
    if (!canSave) return;

    const attrs = { name: name.trim(), formula };
    const saved = field ? { ...field, ...attrs } : addFormulaField(attrs);
    if (field) updateFormulaField(field.id, attrs);

    onSaved?.(saved);
    closeDialog();
  };

  return (
    <Dialog
      size='lg'
      onClose={closeDialog}
      header={<DialogHeader showBorder={true} title={I18n.t(field ? 'edit_formula' : 'create_formula')} onClose={closeDialog} />}
      footer={<DialogFooter addText={I18n.t('save')} onAdd={handleSave} onClose={closeDialog} saveDisabled={!canSave} />}
    >
      <div className='modal-body'>
        <div className='form-group'>
          <label>{I18n.t('name')} *</label>
          <TextInput value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div className='form-group'>
          <label>{I18n.t('formula')} *</label>
          <FormulaEditor value={formula} onChange={setFormula} variables={variables} language={null} height='120px' />
          <p className={`description ${styles['formula-help']}`}>{I18n.t('formula_help_text')}</p>
        </div>
        {formula.trim() !== '' && error && (
          <div className='alert alert-danger'>{error.translate()}</div>
        )}
        {!error && unknownColumns.length > 0 && (
          <div className='alert alert-warning'>
            {I18n.t('formula_error_unknown_column', { columns: unknownColumns.join(', ') })}
          </div>
        )}
      </div>
    </Dialog>
  );
}

export default FormulaFieldDialog;
